import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "@/hooks/use-auth";
import { useLocation } from "wouter";
import { useTranslation } from "@/lib/i18n/i18n";
import { ForgotPasswordDialog } from "@/components/ForgotPasswordDialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { LoginFormSchema, type LoginFormValues } from "@/schemas/auth.schema";
import { Eye, EyeOff, Lock, Mail, User, Shield, Church, ArrowRight } from "lucide-react";

/**
 * Écran de connexion (manuel 2, Action 01, 09/10/2026) : « Saisissez votre
 * adresse électronique et votre mot de passe. Touchez Se connecter. »
 * Ce qui ne marchait pas est retiré : bouton SSO sans effet, case « Se
 * souvenir de moi » non câblée, bloc de comptes de démonstration (route
 * inexistante) et onglet « Créer un compte » (le rôle choisi était ignoré :
 * les comptes de l'équipe sont créés par l'Économat diocésain).
 * « Mot de passe oublié » est branché de bout en bout (code à 6 chiffres
 * reçu par courriel ou SMS, puis nouveau mot de passe).
 */
export default function AuthPage() {
  const t = useTranslation();
  const { user, loginMutation } = useAuth();
  const [, setLocation] = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [resetDone, setResetDone] = useState(false);

  const loginForm = useForm<LoginFormValues>({
    resolver: zodResolver(LoginFormSchema),
    defaultValues: { username: "", password: "" },
  });

  useEffect(() => {
    if (user) {
      setLocation("/dashboard");
    }
  }, [user, setLocation]);

  if (user) {
    return null;
  }

  const handleLogin = loginForm.handleSubmit((values) => {
    loginMutation.mutate(values);
  });

  return (
    <div className="min-h-screen flex font-sans">
      <div
        className="hidden lg:flex lg:w-[55%] flex-col justify-between p-10 relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, var(--brand-navy) 0%, var(--brand-navy-light) 40%, var(--brand-royal-light) 100%)" }}
      >
        <div>
          <img src="/images/logo-white.png" alt="CATHO+ Paroisses" className="h-[250px] mb-4" />
        </div>

        <div className="relative z-10 max-w-lg">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            <h1 className="text-4xl font-bold text-white leading-tight mb-4">
              {t("auth.missionTitle")}
              <br />
              <span className="text-brand-gold">{t("auth.missionTitleAccent")}</span>
            </h1>
            <p className="text-white/60 text-base leading-relaxed mb-8">
              {t("auth.tagline")}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
            className="grid grid-cols-3 gap-3"
          >
            {[
              { icon: Church, label: t("auth.stats.parishesLabel"), sub: t("auth.stats.parishesSub") },
              { icon: User, label: t("auth.stats.faithfulLabel"), sub: t("auth.stats.faithfulSub") },
              { icon: Shield, label: t("auth.stats.secureLabel"), sub: t("auth.stats.secureSub") },
            ].map((item) => (
              <div key={item.label} className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                <item.icon className="w-5 h-5 text-brand-gold mb-2" />
                <div className="text-sm font-semibold text-white">{item.label}</div>
                <div className="text-[11px] text-white/40">{item.sub}</div>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="relative z-10"
        >
          <div className="bg-white/5 rounded-xl px-5 py-4 border border-white/10">
            <p className="text-white text-base italic leading-relaxed font-serif">
              {t("auth.quote")}
            </p>
            <p className="text-white/60 text-xs mt-2 font-medium tracking-wide font-sans">
              {t("auth.quoteRef")}
            </p>
          </div>
        </motion.div>
      </div>

      <div className="flex-1 flex flex-col bg-brand-muted-bg dark:bg-gray-900">
        <div className="flex items-center justify-end p-6 lg:hidden">
          <img src="/images/logo-color.png" alt="CATHO+" className="h-8" />
        </div>

        <div className="flex-1 flex items-center justify-center px-6 py-10">
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="w-full max-w-md"
          >
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-brand-primary dark:text-gray-100 mb-1" data-testid="text-auth-title">
                {t("auth.loginTitle")}
              </h2>
              <p className="text-sm text-gray-400 dark:text-gray-400">
                {t("auth.loginSubtitle")}
              </p>
            </div>

                <Form {...loginForm}>
                <form
                  onSubmit={handleLogin}
                  className="space-y-4"
                >
                  <FormField
                    control={loginForm.control}
                    name="username"
                    render={({ field }) => (
                      <FormItem className="space-y-1.5">
                        <label htmlFor="login-email" className="block text-xs font-medium text-brand-primary dark:text-gray-200">{t("auth.emailLabel")}</label>
                        <div className="relative">
                          <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300 z-10" />
                          <FormControl>
                            <input
                              id="login-email"
                              type="text"
                              // Adresse électronique OU téléphone (19/10/2026) : clavier
                              // ordinaire, pas le clavier des adresses.
                              inputMode="text"
                              data-testid="input-username"
                              autoComplete="username"
                              placeholder={t("auth.emailPlaceholder")}
                              {...field}
                              className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl pl-10 pr-4 py-3 text-sm text-brand-primary dark:text-gray-100 placeholder:text-gray-300 dark:placeholder:text-gray-500 focus:outline-none focus:border-brand-accent focus:ring-1 focus:ring-brand-accent/20 transition-all" />
                          </FormControl>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={loginForm.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem className="space-y-1.5">
                        <label htmlFor="login-password" className="block text-xs font-medium text-brand-primary dark:text-gray-200">{t("auth.password")}</label>
                        <div className="relative">
                          <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300 z-10" />
                          <FormControl>
                            <input
                              id="login-password"
                              type={showPassword ? "text" : "password"}
                              data-testid="input-password"
                              autoComplete="current-password"
                              placeholder="••••••••" aria-label="••••••••"
                              {...field}
                              className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl pl-10 pr-11 py-3 text-sm text-brand-primary dark:text-gray-100 placeholder:text-gray-300 dark:placeholder:text-gray-500 focus:outline-none focus:border-brand-accent focus:ring-1 focus:ring-brand-accent/20 transition-all" />
                          </FormControl>
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500 transition-colors"
                            data-testid="button-toggle-password"
                            aria-label={showPassword ? t("auth.hidePassword") : t("auth.showPassword")}
                          >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="flex items-center justify-end">
                    <button
                      type="button"
                      className="text-xs text-brand-info-blue hover:underline"
                      data-testid="link-forgot-password"
                      onClick={() => { setResetDone(false); setForgotOpen(true); }}
                    >
                      {t("auth.forgotPassword")}
                    </button>
                  </div>
                  {resetDone && (
                    <p className="text-xs text-brand-success bg-brand-success/10 px-3 py-2 rounded-lg" data-testid="text-reset-done">
                      {t("auth.forgot.done")}
                    </p>
                  )}

                  <motion.button
                    type="submit"
                    disabled={loginMutation.isPending}
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full flex items-center justify-center gap-2 rounded-xl py-3.5 text-sm font-semibold text-white transition-all disabled:opacity-60"
                    style={{ backgroundColor: "var(--brand-orange)", boxShadow: "0 4px 14px rgba(230,126,34,0.25)" }}
                    data-testid="button-login"
                  >
                    {loginMutation.isPending ? (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <span>{t("auth.loginButton")}</span>
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </motion.button>
                </form>
                </Form>

            <p className="mt-6 text-xs text-gray-500 dark:text-gray-400 text-center" data-testid="text-login-role-hint">
              {t("auth.roleHint")}
            </p>
            <p className="mt-2 text-xs text-gray-400 dark:text-gray-500 text-center" data-testid="text-no-account">
              {t("auth.accountsByEconomat")}
            </p>

            <div className="mt-8 pt-6 border-t border-gray-100 dark:border-gray-700">
              <div className="flex items-center justify-center gap-4 text-[11px] text-gray-300 dark:text-gray-500">
                <span>{t("auth.footerVersion")}</span>
                <span>-</span>
                <span>{t("auth.footerArchidiocese")}</span>
                <span>-</span>
                <span>{new Date().getFullYear()} EDA</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {forgotOpen && (
        <ForgotPasswordDialog
          initialContact={(() => {
            const saisi = loginForm.getValues("username") ?? "";
            return saisi.includes("@") || /^\+?\d[\d\s]{6,}$/.test(saisi) ? saisi : "";
          })()}
          onClose={() => setForgotOpen(false)}
          onDone={(contact) => {
            setForgotOpen(false);
            setResetDone(true);
            loginForm.setValue("username", contact);
            loginForm.setValue("password", "");
          }}
        />
      )}
    </div>
  );
}
